import { MicroAppCacheKey } from '@/enums/microAppCacheKey'
import { getAllDictReg, getI18nMapReg, getUserInfoReq } from '@/services/micro-app-cache'
import { checkTokenExpired } from '@/utils/request'

function setLocal(key: string, value: any) {
  localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value))
}

function getLocal(key: string) {
  const value = localStorage.getItem(key)
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function clearLocal(key: string) {
  localStorage.removeItem(key)
}

/** 初始化字典数据 */
async function initAllDict() {
  const res: any = await getAllDictReg()
  return res.data
}

/** 初始化国际化数据 */
async function initI18nData() {
  const res: any = await getI18nMapReg({ groupKey: 1 })
  return res.data || {}
}

/** 初始化配送国际化数据 */
async function initDeliveryI18nData() {
  const deliveryI18n: any = {}
  const res: any = await getI18nMapReg({ groupKey: '3' })
  const result: any = res.data || {}
  Object.keys(result).forEach(key => {
    deliveryI18n[`deliveryLocale.${key}`] = result[key]
  })
  return deliveryI18n
}

/** 初始化用户信息 */
async function initUserInfo() {
  const res: any = await getUserInfoReq()
  return res
}

interface State {
  loading: boolean
  allDict: any
  i18nMap: any
  deliveryI18nMap: any
  userInfo: any
  userTimeZone: any
}

const state: State = {
  loading: false,
  // 基础数据不缓存
  allDict: null,
  i18nMap: null,
  deliveryI18nMap: null,
  userInfo: null,
  // 用户时区
  userTimeZone: getLocal(MicroAppCacheKey.UserTimeZone)
}

const mutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading
  },
  clear(state: State) {
    state.allDict = null
    state.i18nMap = null
    state.deliveryI18nMap = null
    state.userInfo = null
    state.userTimeZone = null
    clearLocal(MicroAppCacheKey.AllLookupItemKey)
    clearLocal(MicroAppCacheKey.I18nMapKey)
    clearLocal(MicroAppCacheKey.DeliveryI18nMapKey)
    clearLocal(MicroAppCacheKey.UserInfoKey)
    clearLocal(MicroAppCacheKey.UserTimeZone)
  },
  setAllDict(state: State, data: any) {
    state.allDict = data
    setLocal(MicroAppCacheKey.AllLookupItemKey, data)
  },
  setI18nMap(state: State, data: any) {
    state.i18nMap = data
    setLocal(MicroAppCacheKey.I18nMapKey, data)
  },
  setDeliveryI18nMap(state: State, data: any) {
    state.deliveryI18nMap = data
    setLocal(MicroAppCacheKey.DeliveryI18nMapKey, data)
  },
  setUserInfo(state: State, data: any) {
    state.userInfo = data
    setLocal(MicroAppCacheKey.UserInfoKey, data)

    // 设置默认时区
    const { user = {}, code } = data
    if (!state.userTimeZone && user?.selectedGroup?.groupTimeZone) {
      state.userTimeZone = user?.selectedGroup?.groupTimeZone
      setLocal(MicroAppCacheKey.UserTimeZone, state.userTimeZone)
    }
  },
  setUserTimeZone(state: State, data: any) {
    state.userTimeZone = data
    setLocal(MicroAppCacheKey.UserTimeZone, data)
  }
}

const getters = {
  /** 获取完整字典缓存 */
  getAllDict: (state: State) => {
    return state.allDict
  },
  /** 按 key 获取字典项 */
  getDictByKey: (state: State) => {
    return (key: string) => {
      return state.allDict?.[key]
    }
  },
  getI18nMap: (state: State) => {
    return state.i18nMap
  },
  getDeliveryI18nMap: (state: State) => {
    return state.deliveryI18nMap
  },
  getUserInfo: (state: State) => {
    return state.userInfo
  },
  /** 获取 user 对象 */
  getUser: (state: State) => state.userInfo?.user ?? null,
  getUserGroups: (state: State) => state.userInfo?.user?.groups ?? [],
  getSelectedGroup: (state: State) => state.userInfo?.user?.selectedGroup ?? null,
  getUserName: (state: State) => state.userInfo?.user?.userName ?? '',
  getUserTimeZone: (state: State) => {
    return state.userTimeZone
  }
}

/** 合并接口返回的国际化字段 */
async function loadRemoteI18nIntoVueI18n(mergedMap: any) {
  try {
    if (!mergedMap || typeof mergedMap !== 'object' || !Object.keys(mergedMap).length) return
    const { i18n: i18nStore, setDocumentTitle } = await import('@/lang')
    const locale = i18nStore.global?.locale?.value
    if (!locale) return
    i18nStore.global.mergeLocaleMessage(locale, { ...mergedMap })
    // 词条加载完成后刷新 document.title
    setDocumentTitle()
  } catch (e) {
    console.warn('载入远程国际化词条失败:', e)
  }
}

const actions = {
  async init(context: any) {
    const { state, commit, rootState } = context
    const initStart = Date.now()
    // jumpType=SYS&token 时 getAuth 异步设置 userInfo，轮询等待就绪后再请求（仅改本模块，不动 Application/user-info）
    const maxWait = 15000
    while (!rootState.user?.userInfo && Date.now() - initStart < maxWait) {
      await new Promise<void>(resolve => setTimeout(resolve, 100))
    }
    if (!rootState.user?.userInfo) return

    commit('setLoading', true)
    try {
      // 1. 如果缓存齐全，直接写入 state 并载入国际化，避免再次请求
      if (state.allDict && state.i18nMap && state.deliveryI18nMap && state.userInfo) {
        await loadRemoteI18nIntoVueI18n({ ...state.i18nMap, ...state.deliveryI18nMap })
        return
      }

      // 2. 缓存不齐全时，请求接口前确保 token 有效（过期则刷新）
      const refreshResult = await checkTokenExpired()
      if (refreshResult === false) {
        // 刷新 token 失败，同样抛错
        throw new Error('Token refresh failed, redirecting to login.')
      }

      // 3. 缓存不齐全或强制刷新时，请求接口并写入本地及 state，并载入国际化
      const [allDict, i18nMap, deliveryI18nMap, userInfo] = await Promise.all([
        initAllDict(),
        initI18nData(),
        initDeliveryI18nData(),
        initUserInfo()
      ])

      commit('setAllDict', allDict)
      commit('setI18nMap', i18nMap)
      commit('setDeliveryI18nMap', deliveryI18nMap)
      commit('setUserInfo', userInfo)
      await loadRemoteI18nIntoVueI18n({ ...i18nMap, ...deliveryI18nMap })
    } finally {
      commit('setLoading', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
